// ─────────────────────────────────────────────────────────────
// 사용자 개인 설정 — 브라우저 localStorage 전용 (서버 미저장)
//
// 저장 항목:
//   - geminiApiKey: 본인 Google Gemini API 키 (Blog Coach / SERP / Compare 공용)
//   - defaultBlogUrl: 본인 블로그 URL (Blog Coach / Compare 의 "내 블로그" prefill)
//
// 보안:
//   - 서버 / DB / KV / 로그에 절대 저장하지 않음 (페이지 요청 body 외에 어디에도 안 감)
//   - 키는 사용자가 직접 manage. 입력 폼은 password type 기본
//   - 페이지 prefill 시에도 password type 유지 → 화면 캡처 안전
//
// 사용:
//   const prefs = useUserPrefs();
//   prefs.geminiApiKey; prefs.setGeminiApiKey('AIza...');
//   prefs.defaultBlogUrl; prefs.setDefaultBlogUrl('blog.naver.com/...');
// ─────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';

const LS_GEMINI_KEY = 'keywordgod.userPrefs.geminiApiKey';
const LS_BLOG_URL = 'keywordgod.userPrefs.defaultBlogUrl';

function readLS(key: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeLS(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    /* quota / disabled */
  }
}

/** 페이지 사이 동기화용 커스텀 이벤트 */
const EVT = 'keywordgod-prefs-change';
function emitChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(EVT));
}

export function useUserPrefs() {
  const [geminiApiKey, setGeminiApiKeyState] = useState<string>(() => readLS(LS_GEMINI_KEY));
  const [defaultBlogUrl, setDefaultBlogUrlState] = useState<string>(() => readLS(LS_BLOG_URL));

  // 다른 페이지/탭에서 변경 시 동기화
  useEffect(() => {
    function sync() {
      setGeminiApiKeyState(readLS(LS_GEMINI_KEY));
      setDefaultBlogUrlState(readLS(LS_BLOG_URL));
    }
    window.addEventListener('storage', sync);
    window.addEventListener(EVT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(EVT, sync);
    };
  }, []);

  const setGeminiApiKey = useCallback((v: string) => {
    writeLS(LS_GEMINI_KEY, v);
    setGeminiApiKeyState(v);
    emitChange();
  }, []);

  const setDefaultBlogUrl = useCallback((v: string) => {
    writeLS(LS_BLOG_URL, v);
    setDefaultBlogUrlState(v);
    emitChange();
  }, []);

  const clearAll = useCallback(() => {
    writeLS(LS_GEMINI_KEY, '');
    writeLS(LS_BLOG_URL, '');
    setGeminiApiKeyState('');
    setDefaultBlogUrlState('');
    emitChange();
  }, []);

  return {
    geminiApiKey,
    defaultBlogUrl,
    setGeminiApiKey,
    setDefaultBlogUrl,
    clearAll,
  };
}
